/**
 * SchoolApplyPage - Public student application form
 * No auth required. Allows prospective students/guardians to submit admission applications.
 */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, GraduationCap, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  student_first_name: '',
  student_last_name: '',
  date_of_birth: '',
  gender: '',
  applying_for_class: '',
  previous_school: '',
  guardian_first_name: '',
  guardian_last_name: '',
  guardian_email: '',
  guardian_phone: '',
  guardian_relationship: 'parent',
  address: '',
  notes: '',
};

const SchoolApplyPage = () => {
  const { tenantId } = useParams();
  const [form, setForm] = useState(INITIAL_FORM);
  const [customData, setCustomData] = useState({});
  const [submitted, setSubmitted] = useState(null);

  // Fetch school info (public)
  const { data: school, isLoading: schoolLoading } = useQuery({
    queryKey: ['school-public', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/website`);
      if (!res.ok) throw new Error('School not found');
      return res.json();
    },
    enabled: !!tenantId,
  });

  // Fetch available classes (public)
  const { data: classes = [] } = useQuery({
    queryKey: ['school-apply-classes', tenantId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/apply/classes`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!tenantId,
  });

  // Fetch custom admission form schema (public) — per applying_for_class
  const { data: customForm } = useQuery({
    queryKey: ['school-apply-form', tenantId, form.applying_for_class],
    queryFn: async () => {
      const classMatch = classes.find((c) => c.name === form.applying_for_class);
      const classId = classMatch ? classMatch.id : '';
      const res = await fetch(`${API_URL}/api/school/public/tenants/${tenantId}/admission-form?class_id=${encodeURIComponent(classId)}`);
      if (!res.ok) return { fields: [] };
      return res.json();
    },
    enabled: !!tenantId,
  });

  const customFields = customForm?.fields || [];

  const submitMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Submission failed');
      return res.json();
    },
    onSuccess: (data) => {
      setSubmitted(data);
      toast.success('Application submitted successfully!');
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.student_first_name || !form.student_last_name || !form.guardian_first_name || !form.guardian_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    // Validate required custom fields client-side for friendlier UX
    for (const fld of customFields) {
      if (fld.required) {
        const v = customData[fld.key];
        if (v === undefined || v === '' || v === null || (Array.isArray(v) && v.length === 0)) {
          toast.error(`'${fld.label}' is required`);
          return;
        }
      }
    }
    submitMutation.mutate({ ...form, custom_data: customData, form_id: customForm?.id || '' });
  };

  if (schoolLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const brandColor = school?.primary_color || '#6366f1';
  const logoUrl = school?.logo_url
    ? (school.logo_url.startsWith('http') ? school.logo_url : `${API_URL}${school.logo_url}`)
    : null;

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="application-success">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-10 pb-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Application Submitted!</h2>
            <p className="text-slate-600 text-sm">{submitted.message}</p>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-400">Application Number</p>
              <p className="text-lg font-mono font-bold" style={{ color: brandColor }}
                 data-testid="application-number">{submitted.application_number}</p>
            </div>
            <p className="text-xs text-slate-400">Please save this number for your records.</p>
            <div className="flex gap-3 justify-center pt-2">
              <Link to={`/school/${tenantId}/website`}>
                <Button variant="outline" size="sm">Back to Website</Button>
              </Link>
              <Button variant="outline" size="sm" onClick={() => { setSubmitted(null); setForm(INITIAL_FORM); }}>
                Submit Another
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="school-apply-page">
      {/* Header */}
      <header style={{ backgroundColor: brandColor }} className="py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Link to={`/school/${tenantId}/website`}
                className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4" /> Back to Website
          </Link>
          <div className="flex items-center gap-4">
            {logoUrl && (
              <div className="w-14 h-14 rounded-xl bg-white/20 p-1.5 shrink-0">
                <img src={logoUrl} alt="" className="w-full h-full object-contain rounded-lg" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">Student Application</h1>
              <p className="text-white/80 text-sm">{school?.name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Student Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <GraduationCap className="h-5 w-5" style={{ color: brandColor }} /> Student Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input value={form.student_first_name}
                         onChange={e => setForm({ ...form, student_first_name: e.target.value })}
                         required data-testid="apply-student-first-name" />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input value={form.student_last_name}
                         onChange={e => setForm({ ...form, student_last_name: e.target.value })}
                         required data-testid="apply-student-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date of Birth</Label>
                  <Input type="date" value={form.date_of_birth}
                         onChange={e => setForm({ ...form, date_of_birth: e.target.value })}
                         data-testid="apply-dob" />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={form.gender} onValueChange={v => setForm({ ...form, gender: v })}>
                    <SelectTrigger data-testid="apply-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Applying for Class</Label>
                  <Select value={form.applying_for_class}
                          onValueChange={v => setForm({ ...form, applying_for_class: v })}>
                    <SelectTrigger data-testid="apply-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>
                      {classes.length > 0
                        ? classes.map(c => <SelectItem key={c.id} value={c.name}>{c.name}{c.grade_level ? ` (${c.grade_level})` : ''}</SelectItem>)
                        : <SelectItem value="general">General Admission</SelectItem>
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Previous School</Label>
                  <Input value={form.previous_school}
                         onChange={e => setForm({ ...form, previous_school: e.target.value })}
                         placeholder="Name of previous school" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Guardian Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Guardian / Parent Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input value={form.guardian_first_name}
                         onChange={e => setForm({ ...form, guardian_first_name: e.target.value })}
                         required data-testid="apply-guardian-first-name" />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input value={form.guardian_last_name}
                         onChange={e => setForm({ ...form, guardian_last_name: e.target.value })}
                         data-testid="apply-guardian-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.guardian_email}
                         onChange={e => setForm({ ...form, guardian_email: e.target.value })}
                         placeholder="email@example.com" data-testid="apply-guardian-email" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input value={form.guardian_phone}
                         onChange={e => setForm({ ...form, guardian_phone: e.target.value })}
                         placeholder="+263 77 123 4567" required data-testid="apply-guardian-phone" />
                </div>
              </div>
              <div>
                <Label>Relationship</Label>
                <Select value={form.guardian_relationship}
                        onValueChange={v => setForm({ ...form, guardian_relationship: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="guardian">Guardian</SelectItem>
                    <SelectItem value="sponsor">Sponsor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address}
                       onChange={e => setForm({ ...form, address: e.target.value })}
                       placeholder="Home address" />
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Additional Notes</CardTitle>
              <CardDescription>Any relevant information (medical conditions, special requirements, etc.)</CardDescription>
            </CardHeader>
            <CardContent>
              <textarea className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        value={form.notes}
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                        placeholder="Optional notes..." data-testid="apply-notes" />
            </CardContent>
          </Card>

          {/* Dynamic custom fields from the school's admission form schema */}
          {customFields.length > 0 && (
            <Card data-testid="apply-custom-fields-card">
              <CardHeader>
                <CardTitle className="text-base">{customForm?.name || 'Additional Information'}</CardTitle>
                {customForm?.description && (
                  <CardDescription>{customForm.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {customFields.map((fld) => (
                  <CustomField
                    key={fld.key}
                    field={fld}
                    value={customData[fld.key]}
                    onChange={(v) => setCustomData((prev) => ({ ...prev, [fld.key]: v }))}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <div className="flex justify-end">
            <Button type="submit" size="lg" disabled={submitMutation.isPending}
                    style={{ backgroundColor: brandColor }}
                    className="text-white px-8 hover:opacity-90"
                    data-testid="submit-application-btn">
              {submitMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Submit Application
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SchoolApplyPage;


// Renders one dynamic custom field of any supported type.
function CustomField({ field, value, onChange }) {
  const id = `cf-${field.key}`;
  const label = (
    <Label htmlFor={id} className="flex items-center gap-1">
      {field.label}
      {field.required && <span className="text-rose-600">*</span>}
    </Label>
  );
  const help = field.helptext && <p className="text-xs text-slate-500 mt-1">{field.helptext}</p>;

  const t = field.type || 'text';
  if (t === 'textarea') {
    return (
      <div>
        {label}
        <textarea id={id}
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={field.placeholder || ''}
                  className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  data-testid={`apply-custom-${field.key}`} />
        {help}
      </div>
    );
  }
  if (t === 'select') {
    return (
      <div>
        {label}
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger id={id} data-testid={`apply-custom-${field.key}`}>
            <SelectValue placeholder={field.placeholder || 'Select'} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {help}
      </div>
    );
  }
  if (t === 'checkbox') {
    return (
      <div>
        <label className="flex items-center gap-2 text-sm" htmlFor={id}>
          <input id={id} type="checkbox" checked={!!value}
                 onChange={(e) => onChange(e.target.checked)}
                 className="rounded border-slate-300"
                 data-testid={`apply-custom-${field.key}`} />
          <span>{field.label}{field.required && <span className="text-rose-600"> *</span>}</span>
        </label>
        {help}
      </div>
    );
  }
  const inputType = ({ number: 'number', date: 'date', email: 'email', phone: 'tel', file: 'text' })[t] || 'text';
  return (
    <div>
      {label}
      <Input id={id}
             type={inputType}
             value={value || ''}
             onChange={(e) => onChange(e.target.value)}
             placeholder={field.placeholder || ''}
             data-testid={`apply-custom-${field.key}`} />
      {help}
    </div>
  );
}
